/* eslint-disable react/jsx-one-expression-per-line */
import React from 'react';
import PropTypes from 'prop-types';
import {
  ButtonIcon,
  Icon,
  Modal,
  ModalHeader,
  Typography,
} from '@statsbomb/kitbag-components';
import { useReactiveVar } from '@apollo/client';
import VideoPanel from './VideoPanel';
import { ModalStyleWrapper, VideoModalHeader } from './VideoModal.styles';
import LoaderLabelled from '../LoaderLabelled/LoaderLabelled';
import { useGetStatsVideo } from '../../utils/hooks/useGetStatsVideo';
import { ErrorPageContent } from '../ErrorPage/ErrorPage.styles';
import ExternalLink from '../ExternalLink/ExternalLink';
import { getRosterPositionColorByCode } from '../../utils/helpers/positions';
import { getPalette } from '../../utils/visualisations/visPalettes';
import { ui_isDark } from '../../apollo';
import { ROSTER_POSITIONS } from '../../utils/constants/positions';
import { useStore } from '../../store/useStore';
import { VIDEO_SPEED_THRESHOLD } from './VideoModal.constants';
import { getFormattedVideoTitle } from './VideoModal.helpers';

const VideoModal = ({ onClose }) => {
  const videoModalOpen = useStore((s) => s.videoModalOpen);
  const selectedStatName = useStore((s) => s.selectedStatName);
  const selectedPlayerId = useStore((s) => s.selectedPlayerId);
  const selectedTeamId = useStore((s) => s.selectedTeamId);
  const selectedPosition = useStore((s) => s.selectedPosition);
  const selectedGameId = useStore((s) => s.selectedGameId);
  const title = useStore((s) => s.selectedTitle);
  const ui_isDarkRV = useReactiveVar(ui_isDark);
  const visPalette = getPalette(ui_isDarkRV);

  const { hasError, isLoading, data } = useGetStatsVideo(
    selectedTeamId,
    selectedPlayerId,
    selectedStatName,
    selectedPosition,
    selectedGameId
  );

  const playIdObjects = Array.isArray(data?.statsVideo)
    ? data.statsVideo.map((obj) => ({
        playId: String(obj.playId),
        highlightPlayers: [
          {
            active: true,
            id: parseInt(selectedPlayerId, 10),
            colour: getRosterPositionColorByCode(
              ROSTER_POSITIONS[selectedPosition].code,
              visPalette
            ),
            name: title.split(',')[0],
            number: 0,
            physicalMetrics: {
              speed: {
                active: true,
                threshold: VIDEO_SPEED_THRESHOLD,
              },
            },
          },
        ],
      }))
    : [];

  const formattedTitle = getFormattedVideoTitle(title, data?.statsVideo);

  return (
    <ModalStyleWrapper>
      <Modal isOpen={videoModalOpen} onClose={onClose} onEscape={onClose}>
        <div>
          <VideoModalHeader>
            {!isLoading && !hasError && (
              <ModalHeader testId="video-modal-header">
                {formattedTitle}
              </ModalHeader>
            )}
            <div className="close">
              <ButtonIcon
                onClick={onClose}
                variant="ghost"
                shape="pill"
                size="small"
                icon="Close"
                title="Cancel"
              />
            </div>
          </VideoModalHeader>

          {isLoading && (
            <div id="videoPanel" style={{ height: 'calc(100% - 50px)' }}>
              <LoaderLabelled
                text="Loading Video Data"
                height="calc(100% - 10px)"
              />
            </div>
          )}
          {hasError && (
            <div id="videoPanel">
              <ErrorPageContent>
                <Icon variant="Warning" colour="messaging.warning" />
                <Typography variant="headingLarge" data-testid="error-header">
                  Unable to Load Videos
                </Typography>
                <Typography variant="bodyRegular">
                  Try closing the modal and try again. If the problem persists,{' '}
                  <ExternalLink href={process.env.SUPPORT_URL}>
                    contact support
                  </ExternalLink>
                  .
                </Typography>
              </ErrorPageContent>
            </div>
          )}

          <VideoPanel data={playIdObjects} isModal fixedPlaylist />
        </div>
      </Modal>
    </ModalStyleWrapper>
  );
};

VideoModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default VideoModal;
